import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  BookOpen, 
  ExternalLink, 
  Search, 
  ShoppingCart,
  ArrowLeft,
  Loader2,
  BookMarked
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to normalize image URLs - handles old preview URLs and relative paths
const getImageUrl = (url) => {
  if (!url) return null;
  // Already a relative path - prepend API URL
  if (url.startsWith('/api/uploads')) return `${API}${url}`;
  if (url.startsWith('/uploads')) return `${API}/api${url}`;
  // Old preview URL - extract path and use current API
  if (url.includes('.preview.emergentagent.com')) {
    const match = url.match(/\/api\/uploads\/.+$/);
    if (match) return `${API}${match[0]}`;
    const uploadMatch = url.match(/\/uploads\/.+$/);
    if (uploadMatch) return `${API}/api${uploadMatch[0]}`;
  }
  // External URL (http/https) - return as-is
  if (url.startsWith('http')) return url;
  // Relative path without leading slash
  return `${API}/${url}`;
};

export default function StudyGuides() {
  const navigate = useNavigate();
  const [guides, setGuides] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchGuides();
    fetchCategories();
  }, []);

  const fetchGuides = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      
      const res = await fetch(`${API}/api/study-guides?${params}`);
      if (res.ok) {
        const data = await res.json();
        setGuides(data.guides || []);
      }
    } catch (err) {
      console.error('Failed to fetch guides:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await fetch(`${API}/api/study-guides/categories`);
      if (res.ok) {
        const data = await res.json();
        setCategories(data || []);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchGuides();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, selectedCategory]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-slate-600 hover:text-slate-900 mb-4"
            data-testid="back-home-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-lg">
              <BookMarked className="w-8 h-8 text-amber-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Shop</h1>
              <p className="text-slate-500">Curated resources to support your learning journey</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search guides..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-white border-slate-200"
              data-testid="search-guides-input"
            />
          </div>
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('all')}
                className={selectedCategory === 'all' ? 'bg-slate-900' : 'border-slate-200'}
              >
                All
              </Button>
              {categories.map((cat) => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                  className={selectedCategory === cat ? 'bg-slate-900' : 'border-slate-200'}
                >
                  {cat}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400 mb-2" />
            <p className="text-slate-500">Loading guides...</p>
          </div>
        ) : guides.length === 0 ? (
          <Card className="bg-white border-slate-200">
            <CardContent className="py-12 text-center">
              <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Products Available</h3>
              <p className="text-slate-500">
                {search || selectedCategory !== 'all' 
                  ? 'Try adjusting your search or filters.' 
                  : 'Check back soon for new resources!'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {guides.map((guide) => (
              <Card 
                key={guide.guide_id} 
                className="bg-white border-slate-200 hover:shadow-lg transition-shadow overflow-hidden"
                data-testid={`guide-card-${guide.guide_id}`}
              >
                {/* Cover Image */}
                {guide.cover_image_url ? (
                  <div className="aspect-[3/4] bg-slate-100 overflow-hidden">
                    <img 
                      src={getImageUrl(guide.cover_image_url)} 
                      alt={guide.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
                        e.target.parentElement.innerHTML = '<svg class="w-16 h-16 text-amber-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>';
                      }}
                    />
                  </div>
                ) : (
                  <div className="aspect-[3/4] bg-gradient-to-br from-amber-50 to-orange-100 flex items-center justify-center">
                    <BookOpen className="w-16 h-16 text-amber-300" />
                  </div>
                )}
                
                <CardContent className="p-4">
                  {guide.category && (
                    <Badge variant="outline" className="mb-2 text-xs bg-amber-50 text-amber-700 border-amber-200">
                      {guide.category}
                    </Badge>
                  )}
                  <h3 className="font-semibold text-slate-900 mb-2 line-clamp-2">{guide.title}</h3>
                  {guide.description && (
                    <p className="text-sm text-slate-500 mb-4 line-clamp-3">{guide.description}</p>
                  )}
                  <Button 
                    onClick={() => window.open(guide.amazon_url, '_blank', 'noopener,noreferrer')}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                    data-testid={`view-amazon-${guide.guide_id}`}
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    View on Amazon
                    <ExternalLink className="w-3 h-3 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Info Banner */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-slate-100 rounded-xl p-6 text-center">
          <h3 className="font-semibold text-slate-900 mb-2">Looking for more resources?</h3>
          <p className="text-slate-600 mb-4">
            Explore our interactive quizzes, games, and case studies to reinforce your learning.
          </p>
          <Button onClick={() => navigate('/dashboard')} className="bg-teal-600 hover:bg-teal-700 text-white">
            Go to Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
